import { useOrderHistoryControllerGetOrderHistoryForProduct } from "@/api/types/main";
import { GetOrderHistoryForProductResponseDTO, NumberCeilDTO } from "@/api/types/main/schemas";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { TanstackTable } from "@/components/custom-ui/tables/tanstack.table";
import {
  MemoizedTanstackTableBody,
  TanstackTableBody,
} from "@/components/custom-ui/tables/tanstack.table.body";
import {
  MemoizedTanstackTableHeader,
  TanstackTableHeader,
} from "@/components/custom-ui/tables/tanstack.table.header";
import { DndSortableHeadersContext } from "@/components/tables/dnd.sortable.headers.context";
import { useCommersCardColumns } from "@/hooks/useCommersCardColumns";
import UseGetFlatRowsTable from "@/hooks/useGetFlatRowsTable";
import { useTableDataRangeStore } from "@/store/table.data.range";
import { TableIds } from "@/store/tables";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { TransformCardData } from "./commers.card.rating";

const READABLE_ORDERS_CARD_LABELS: Partial<
  Record<keyof GetOrderHistoryForProductResponseDTO, string>
> = {
  ordersAmount: "Количество заказов",
  ordersWithoutReturnsAmount: "Количество заказов без возвратов",
  returnAmount: "Количество возвратов",
  selfPurchaseAmount: "Количество самовыкупов",
} as const;

type OrdersCardField = keyof typeof READABLE_ORDERS_CARD_LABELS;

export const CommersCardOrders = () => {
  const dateRange = useTableDataRangeStore((store) => store.dateRange);
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { data, isLoading, isError, error } = useOrderHistoryControllerGetOrderHistoryForProduct(
    productId,
    {
      periodEnd: format(dateRange.to, "yyyy-MM-dd"),
      periodStart: format(dateRange.from, "yyyy-MM-dd"),
    },
    {
      query: {
        queryKey: ["card", "orders", dateRange.from, dateRange.to],
        select: (responseData: GetOrderHistoryForProductResponseDTO): TransformCardData[] =>
          Object.entries(READABLE_ORDERS_CARD_LABELS)
            .filter(([field]) => {
              const value = responseData[field as OrdersCardField];
              return field in responseData && Array.isArray(value);
            })
            .map(([field, label]) => ({
              label,
              data: responseData[field as OrdersCardField] as NumberCeilDTO[],
            })),
      },
    }
  );

  const columns = useCommersCardColumns({ dateRange: dateRange });

  const { table, columnSizeVars, columnOrder } = UseGetFlatRowsTable({
    data: data || [],
    columns,
    tableId: TableIds.Commers_OrdersCard,
  });
  if (isError) return <div>{error?.message}</div>;
  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="h-full flex flex-col">
      <TanstackTable style={{ width: table.getTotalSize(), ...columnSizeVars }} className="flex-1">
        {table.getState().columnSizingInfo.isResizingColumn ? (
          <>
            <MemoizedTanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
            <MemoizedTanstackTableBody table={table} columnOrder={columnOrder} />
          </>
        ) : (
          <>
            <DndSortableHeadersContext table={table} tableId={TableIds.Commers_OrdersCard}>
              <TanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
              <TanstackTableBody table={table} columnOrder={columnOrder} />
            </DndSortableHeadersContext>
          </>
        )}
      </TanstackTable>
    </div>
  );
};
